import React, { useState, useRef, useEffect } from 'react';
import { User } from '../../services/authService';
import { HindiService, HindiLesson, HindiWord, LessonSummary, ProficiencyLevel, extractLevelFromObjective, getLevelDisplayName } from '../../services/hindiService';
import PageHeader from '../common/PageHeader';
import GenericHistorySelector from '../shared/GenericHistorySelector';
import GenericPastLessons from '../shared/GenericPastLessons';
import { useSpeech } from '../../hooks/useSpeech';
import './HindiReading.css';

interface HindiReadingProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

type RomanizationMode = 'hidden' | 'visible';
type LoadingStage = 'idle' | 'text' | 'vocabulary';
type TabType = 'text' | 'vocabulary' | 'alphabet';
type ViewMode = 'current' | 'past-list' | 'past-lesson';

interface AlphabetLetter {
  character: string;
  transliteration: string;
  pronunciation: string;
}

// Static Devanagari Alphabet Data
const DEVANAGARI_VOWELS: AlphabetLetter[] = [
  { character: 'अ', transliteration: 'a', pronunciation: 'like "u" in "but"' },
  { character: 'आ', transliteration: 'ā', pronunciation: 'like "a" in "father"' },
  { character: 'इ', transliteration: 'i', pronunciation: 'like "i" in "bit"' },
  { character: 'ई', transliteration: 'ī', pronunciation: 'like "ee" in "feet"' },
  { character: 'उ', transliteration: 'u', pronunciation: 'like "u" in "put"' },
  { character: 'ऊ', transliteration: 'ū', pronunciation: 'like "oo" in "boot"' },
  { character: 'ऋ', transliteration: 'ṛ', pronunciation: 'like "ri" in "risk"' },
  { character: 'ए', transliteration: 'e', pronunciation: 'like "ay" in "say"' },
  { character: 'ऐ', transliteration: 'ai', pronunciation: 'like "ai" in "aisle"' },
  { character: 'ओ', transliteration: 'o', pronunciation: 'like "o" in "go"' },
  { character: 'औ', transliteration: 'au', pronunciation: 'like "ow" in "cow"' },
  { character: 'अं', transliteration: 'aṃ', pronunciation: 'nasal sound' },
  { character: 'अः', transliteration: 'aḥ', pronunciation: 'aspirated sound' },
];

const DEVANAGARI_CONSONANTS: AlphabetLetter[] = [
  // क वर्ग (Velar)
  { character: 'क', transliteration: 'ka', pronunciation: 'like "k" in "kite"' },
  { character: 'ख', transliteration: 'kha', pronunciation: 'like "kh" in "blockhead"' },
  { character: 'ग', transliteration: 'ga', pronunciation: 'like "g" in "go"' },
  { character: 'घ', transliteration: 'gha', pronunciation: 'like "gh" in "doghouse"' },
  { character: 'ङ', transliteration: 'ṅa', pronunciation: 'like "ng" in "sing"' },
  // च वर्ग (Palatal)
  { character: 'च', transliteration: 'ca', pronunciation: 'like "ch" in "church"' },
  { character: 'छ', transliteration: 'cha', pronunciation: 'aspirated "ch"' },
  { character: 'ज', transliteration: 'ja', pronunciation: 'like "j" in "jump"' },
  { character: 'झ', transliteration: 'jha', pronunciation: 'aspirated "j"' },
  { character: 'ञ', transliteration: 'ña', pronunciation: 'like "ny" in "canyon"' },
  // ट वर्ग (Retroflex)
  { character: 'ट', transliteration: 'ṭa', pronunciation: 'retroflex "t"' },
  { character: 'ठ', transliteration: 'ṭha', pronunciation: 'aspirated retroflex "t"' },
  { character: 'ड', transliteration: 'ḍa', pronunciation: 'retroflex "d"' },
  { character: 'ढ', transliteration: 'ḍha', pronunciation: 'aspirated retroflex "d"' },
  { character: 'ण', transliteration: 'ṇa', pronunciation: 'retroflex "n"' },
  // त वर्ग (Dental)
  { character: 'त', transliteration: 'ta', pronunciation: 'like "t" in "table"' },
  { character: 'थ', transliteration: 'tha', pronunciation: 'aspirated "t"' },
  { character: 'द', transliteration: 'da', pronunciation: 'like "d" in "dog"' },
  { character: 'ध', transliteration: 'dha', pronunciation: 'aspirated "d"' },
  { character: 'न', transliteration: 'na', pronunciation: 'like "n" in "no"' },
  // प वर्ग (Labial)
  { character: 'प', transliteration: 'pa', pronunciation: 'like "p" in "pet"' },
  { character: 'फ', transliteration: 'pha', pronunciation: 'aspirated "p"' },
  { character: 'ब', transliteration: 'ba', pronunciation: 'like "b" in "ball"' },
  { character: 'भ', transliteration: 'bha', pronunciation: 'aspirated "b"' },
  { character: 'म', transliteration: 'ma', pronunciation: 'like "m" in "mother"' },
  // Other consonants
  { character: 'य', transliteration: 'ya', pronunciation: 'like "y" in "yes"' },
  { character: 'र', transliteration: 'ra', pronunciation: 'like "r" in "run"' },
  { character: 'ल', transliteration: 'la', pronunciation: 'like "l" in "love"' },
  { character: 'व', transliteration: 'va', pronunciation: 'like "v" in "voice"' },
  { character: 'श', transliteration: 'śa', pronunciation: 'like "sh" in "ship"' },
  { character: 'ष', transliteration: 'ṣa', pronunciation: 'retroflex "sh"' },
  { character: 'स', transliteration: 'sa', pronunciation: 'like "s" in "sun"' },
  { character: 'ह', transliteration: 'ha', pronunciation: 'like "h" in "hot"' },
  { character: 'क्ष', transliteration: 'kṣa', pronunciation: 'like "ksh"' },
  { character: 'त्र', transliteration: 'tra', pronunciation: 'like "tr"' },
  { character: 'ज्ञ', transliteration: 'jña', pronunciation: 'like "gya"' },
];

const HindiReading: React.FC<HindiReadingProps> = ({
  user,
  onClose,
  onToggleSidebar
}) => {
  const [lesson, setLesson] = useState<HindiLesson | null>(null);
  const [selectedWord, setSelectedWord] = useState<HindiWord | null>(null);
  const [romanizationMode, setRomanizationMode] = useState<RomanizationMode>('hidden');
  const [loadingStage, setLoadingStage] = useState<LoadingStage>('idle');
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('text');
  const [showMenu, setShowMenu] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('current');
  const [selectedPastLesson, setSelectedPastLesson] = useState<HindiLesson | null>(null);
  const [showHistorySelector, setShowHistorySelector] = useState(false);
  const [pastLessonsList, setPastLessonsList] = useState<LessonSummary[]>([]);
  const [speakingAlphabetIndex, setSpeakingAlphabetIndex] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Speech synthesis hook
  const { speak, stop, isSpeaking, isSupported: isSpeechSupported } = useSpeech({ lang: 'en-IN', rate: 0.8 });

  // Load latest lesson from database on mount
  useEffect(() => {
    loadLatestLesson();
  }, []);

  const loadLatestLesson = async () => {
    try {
      const result = await HindiService.getPastLessons(1);
      if (result.lessons.length > 0) {
        const latestLesson = await HindiService.getLessonById(result.lessons[0].lesson_id);
        setLesson(latestLesson);
      }
    } catch (err) {
      console.error('Error loading latest lesson:', err);
      // It's okay if there are no lessons yet
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const handleOpenGenerateLesson = async () => {
    try {
      setShowMenu(false);
      // Fetch past lessons for the selector
      const result = await HindiService.getPastLessons(100);
      setPastLessonsList(result.lessons);
      setShowHistorySelector(true);
    } catch (err) {
      console.error('Error loading past lessons:', err);
      // Open selector anyway, it will show empty state
      setPastLessonsList([]);
      setShowHistorySelector(true);
    }
  };

  const handleGenerateLesson = async (level: ProficiencyLevel, selectedIds: string[], customInstruction: string) => {
    try {
      setShowHistorySelector(false);
      setLoadingStage('text');
      setError(null);
      setSelectedWord(null);
      
      // Generate lesson with progress tracking
      const newLesson = await HindiService.generateLesson(
        level,
        selectedIds,
        customInstruction || undefined,
        (stage) => {
          setLoadingStage(stage);
        }
      );
      
      setLesson(newLesson);
      setLoadingStage('idle');
      setActiveTab('text');
      setViewMode('current');
    } catch (err) {
      console.error('Error generating lesson:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate lesson. Please try again.');
      setLoadingStage('idle');
    }
  };

  const handleOpenPastLessons = () => {
    setShowMenu(false);
    setViewMode('past-list');
  };

  const handleSelectPastLesson = (pastLesson: HindiLesson) => {
    setSelectedPastLesson(pastLesson);
    setViewMode('past-lesson');
    setActiveTab('text');
  };

  const handleBackToCurrentLesson = () => {
    setViewMode('current');
    setSelectedPastLesson(null);
  };

  const toggleRomanizationMode = () => {
    setRomanizationMode(current => current === 'hidden' ? 'visible' : 'hidden');
    setShowMenu(false);
  };

  const handleWordClick = (word: HindiWord) => {
    setSelectedWord(word);
  };

  // Get transliteration for a single character
  const getCharacterTransliteration = (char: string): string | null => {
    // Create a map of all characters to their transliterations
    const allLetters = [...DEVANAGARI_VOWELS, ...DEVANAGARI_CONSONANTS];
    const charMap = new Map<string, string>();
    allLetters.forEach(letter => {
      charMap.set(letter.character, letter.transliteration);
    });
    
    return charMap.get(char) || null;
  };

  // Handle alphabet pronunciation
  const handleAlphabetSpeak = (letter: AlphabetLetter, index: number, category: 'vowel' | 'consonant') => {
    const key = `${category}-${index}`;
    if (speakingAlphabetIndex === key && isSpeaking) {
      stop();
      setSpeakingAlphabetIndex(null);
    } else {
      setSpeakingAlphabetIndex(key);
      speak(letter.transliteration);
      // Clear speaking state after speech ends
      setTimeout(() => {
        if (!isSpeaking) {
          setSpeakingAlphabetIndex(null);
        }
      }, 1000);
    }
  };

  // Handle text lesson playback
  const handleTextPlayback = () => {
    const currentLesson = getCurrentLesson();
    if (!currentLesson) return;

    if (isSpeaking) {
      stop();
    } else {
      // Convert Hindi text to transliteration
      const transliteratedText = convertToTransliteration(currentLesson.text, currentLesson.words);
      speak(transliteratedText);
    }
  };

  // Convert Hindi text to transliteration using word mappings
  const convertToTransliteration = (text: string, words: HindiWord[]): string => {
    let result = text;
    
    // Create a map of Hindi characters to transliterations
    const transliterationMap = new Map<string, string>();
    words.forEach(word => {
      transliterationMap.set(word.character, word.transliteration);
    });

    // Replace Hindi words with their transliterations
    words.forEach(word => {
      const regex = new RegExp(word.character, 'g');
      result = result.replace(regex, word.transliteration);
    });

    return result;
  };

  // Get the current lesson being displayed
  const getCurrentLesson = () => {
    if (viewMode === 'past-lesson' && selectedPastLesson) {
      return selectedPastLesson;
    }
    return lesson;
  };

  // Split lesson text into clickable words
  const renderLessonText = () => {
    const currentLesson = getCurrentLesson();
    if (!currentLesson) return null;

    const { text, words } = currentLesson;
    const segments: JSX.Element[] = [];
    let currentIndex = 0;

    // Create a map for quick word lookup
    const wordMap = new Map<string, HindiWord>();
    words.forEach(word => wordMap.set(word.character, word));

    // Try to match words in order they appear in text
    words.forEach((word, idx) => {
      const wordIndex = text.indexOf(word.character, currentIndex);
      
      if (wordIndex >= currentIndex) {
        // Add any text before this word
        if (wordIndex > currentIndex) {
          const beforeText = text.substring(currentIndex, wordIndex);
          segments.push(
            <span key={`text-${currentIndex}`} className="hindi-text-plain">
              {beforeText}
            </span>
          );
        }

        // Add the clickable word
        segments.push(
          <span
            key={`word-${idx}`}
            className={`hindi-word ${romanizationMode === 'visible' ? 'show-romanization' : ''}`}
            onClick={() => handleWordClick(word)}
            data-romanization={word.transliteration}
          >
            {word.character}
          </span>
        );

        currentIndex = wordIndex + word.character.length;
      }
    });

    // Add any remaining text
    if (currentIndex < text.length) {
      segments.push(
        <span key={`text-${currentIndex}`} className="hindi-text-plain">
          {text.substring(currentIndex)}
        </span>
      );
    }

    return <div className="hindi-text-content">{segments}</div>;
  };

  // Determine current lesson and view state
  const currentLesson = getCurrentLesson();
  const showBackButton = viewMode === 'past-lesson';

  return (
    <div className="hindi-learn-container">
      {/* If in past lessons list view, show GenericPastLessons component */}
      {viewMode === 'past-list' && (
        <GenericPastLessons
          service={HindiService}
          onSelectLesson={handleSelectPastLesson}
          onClose={handleBackToCurrentLesson}
        />
      )}

      {/* Main lesson view */}
      {viewMode !== 'past-list' && (
        <>
      {/* Page Header */}
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        leftActions={
          showBackButton ? (
            <button 
              className="page-header-icon-btn hindi-back-btn"
              onClick={handleBackToCurrentLesson}
              aria-label="Back to current lesson"
            >
              ←
            </button>
          ) : undefined
        }
        centerContent={
          <h1 className="page-header-center-title">
            {viewMode === 'past-lesson' ? 'Past Lesson' : 'Hindi Learning'}
          </h1>
        }
        rightActions={
          <div className="hindi-menu-container" ref={menuRef}>
            <button 
              className="page-header-icon-btn"
              onClick={() => setShowMenu(!showMenu)}
              disabled={loadingStage !== 'idle'}
            >
              ⋮
            </button>
            {showMenu && (
              <div className="hindi-menu-dropdown">
                <button
                  className="hindi-menu-item"
                  onClick={handleOpenGenerateLesson}
                >
                  ✨ Generate New Lesson
                </button>
                <button
                  className="hindi-menu-item"
                  onClick={handleOpenPastLessons}
                >
                  📚 Past Lessons
                </button>
                {currentLesson && (
                  <button
                    className="hindi-menu-item"
                    onClick={toggleRomanizationMode}
                  >
                    {romanizationMode === 'hidden' ? '👁️ Show Romanization' : '👁️‍🗨️ Hide Romanization'}
                  </button>
                )}
              </div>
            )}
          </div>
        }
      />

      {/* Main Content */}
      <div className="hindi-main-content">
        {/* Loading State */}
        {loadingStage !== 'idle' && (
          <div className="hindi-loading">
            <div className="hindi-spinner-large"></div>
            <p className="hindi-loading-text">
              {loadingStage === 'text' ? 'Step 1/2: Generating text...' : 'Step 2/2: Analyzing vocabulary...'}
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && loadingStage === 'idle' && (
          <div className="hindi-error">
            <p>{error}</p>
          </div>
        )}

        {/* Lesson Content - Text Tab */}
        {currentLesson && loadingStage === 'idle' && activeTab === 'text' && (
          <div className="hindi-chapter">
            <h1 className="hindi-lesson-title">{currentLesson.lesson_name}</h1>
            <div className="hindi-lesson-objective">
              {currentLesson.lesson_objective.replace(/\[Level:\s*\w+\]\s*/, '')}
            </div>

            {/* Audio Player Controls */}
            {isSpeechSupported && (
              <div className="hindi-audio-player">
                <button
                  className={`hindi-audio-btn ${isSpeaking ? 'playing' : ''}`}
                  onClick={handleTextPlayback}
                  aria-label={isSpeaking ? 'Stop reading' : 'Read text aloud'}
                >
                  {isSpeaking ? (
                    <>
                      <span className="hindi-audio-icon">⏸</span>
                      <span className="hindi-audio-label">Stop</span>
                    </>
                  ) : (
                    <>
                      <span className="hindi-audio-icon">▶</span>
                      <span className="hindi-audio-label">Listen to Text</span>
                    </>
                  )}
                </button>
                {isSpeaking && <span className="hindi-audio-indicator">Playing...</span>}
              </div>
            )}

            <div className="hindi-text-content">
              {renderLessonText()}
            </div>
            <div className="hindi-instructions">
              💡 Click on any word to see its translation.
            </div>
          </div>
        )}

        {/* Vocabulary Tab */}
        {currentLesson && loadingStage === 'idle' && activeTab === 'vocabulary' && (
          <div className="hindi-vocabulary-section">
            <h2 className="hindi-vocab-section-header">Vocabulary ({currentLesson.words.length} words)</h2>
            <div className="hindi-vocab-grid">
              {currentLesson.words.map((word, index) => {
                const key = `vocab-${index}`;
                const isCurrentlySpeaking = speakingAlphabetIndex === key && isSpeaking;
                return (
                  <div key={index} className={`hindi-vocab-item-wrapper ${isCurrentlySpeaking ? 'speaking' : ''}`}>
                    <button
                      className={`hindi-vocab-item ${selectedWord?.character === word.character ? 'active' : ''}`}
                      onClick={() => handleWordClick(word)}
                    >
                      <span className="hindi-vocab-char">{word.character}</span>
                      <span className="hindi-vocab-trans">{word.transliteration}</span>
                    </button>
                    {isSpeechSupported && (
                      <button
                        className="hindi-vocab-speaker"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isCurrentlySpeaking) {
                            stop();
                            setSpeakingAlphabetIndex(null);
                          } else {
                            setSpeakingAlphabetIndex(key);
                            speak(word.transliteration);
                            setTimeout(() => {
                              if (!isSpeaking) {
                                setSpeakingAlphabetIndex(null);
                              }
                            }, 1000);
                          }
                        }}
                        aria-label={`Pronounce ${word.character}`}
                      >
                        {isCurrentlySpeaking ? '⏸' : '🔊'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Alphabet Tab */}
        {activeTab === 'alphabet' && loadingStage === 'idle' && (
          <div className="hindi-alphabet-section">
            <div className="hindi-alphabet-category">
              <h2 className="hindi-alphabet-category-header">Vowels (स्वर)</h2>
              <div className="hindi-alphabet-grid">
                {DEVANAGARI_VOWELS.map((letter, index) => {
                  const key = `vowel-${index}`;
                  const isCurrentlySpeaking = speakingAlphabetIndex === key && isSpeaking;
                  return (
                    <div key={index} className={`hindi-alphabet-card ${isCurrentlySpeaking ? 'speaking' : ''}`}>
                      {isSpeechSupported && (
                        <button
                          className="hindi-alphabet-speaker"
                          onClick={() => handleAlphabetSpeak(letter, index, 'vowel')}
                          aria-label={`Pronounce ${letter.character}`}
                        >
                          {isCurrentlySpeaking ? '⏸' : '🔊'}
                        </button>
                      )}
                      <div className="hindi-alphabet-char">{letter.character}</div>
                      <div className="hindi-alphabet-trans">{letter.transliteration}</div>
                      <div className="hindi-alphabet-pronunciation">{letter.pronunciation}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="hindi-alphabet-category">
              <h2 className="hindi-alphabet-category-header">Consonants (व्यंजन)</h2>
              <div className="hindi-alphabet-grid">
                {DEVANAGARI_CONSONANTS.map((letter, index) => {
                  const key = `consonant-${index}`;
                  const isCurrentlySpeaking = speakingAlphabetIndex === key && isSpeaking;
                  return (
                    <div key={index} className={`hindi-alphabet-card ${isCurrentlySpeaking ? 'speaking' : ''}`}>
                      {isSpeechSupported && (
                        <button
                          className="hindi-alphabet-speaker"
                          onClick={() => handleAlphabetSpeak(letter, index, 'consonant')}
                          aria-label={`Pronounce ${letter.character}`}
                        >
                          {isCurrentlySpeaking ? '⏸' : '🔊'}
                        </button>
                      )}
                      <div className="hindi-alphabet-char">{letter.character}</div>
                      <div className="hindi-alphabet-trans">{letter.transliteration}</div>
                      <div className="hindi-alphabet-pronunciation">{letter.pronunciation}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Welcome Message */}
        {!currentLesson && loadingStage === 'idle' && !error && (
          <div className="hindi-welcome">
            <h2>Welcome to Hindi Learning</h2>
            <p>Click the menu (⋮) in the top right and select "Generate New Lesson" to create an AI-powered Hindi reading lesson.</p>
            <div className="hindi-features">
              <div className="hindi-feature">
                <span className="hindi-feature-icon">📖</span>
                <span>Beginner to Advanced Levels</span>
              </div>
              <div className="hindi-feature">
                <span className="hindi-feature-icon">🔤</span>
                <span>Interactive Word Translations</span>
              </div>
              <div className="hindi-feature">
                <span className="hindi-feature-icon">🎯</span>
                <span>Devanagari & Romanization Support</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Tab Bar */}
      {loadingStage === 'idle' && (
        <div className="hindi-bottom-bar">
          {currentLesson && (
            <>
              <button
                className={`hindi-tab-button ${activeTab === 'text' ? 'active' : ''}`}
                onClick={() => setActiveTab('text')}
              >
                Text
              </button>
              <button
                className={`hindi-tab-button ${activeTab === 'vocabulary' ? 'active' : ''}`}
                onClick={() => setActiveTab('vocabulary')}
              >
                Vocabulary ({currentLesson.words.length})
              </button>
            </>
          )}
          <button
            className={`hindi-tab-button ${activeTab === 'alphabet' ? 'active' : ''}`}
            onClick={() => setActiveTab('alphabet')}
          >
            Alphabet
          </button>
        </div>
      )}

      {/* History Selector Modal */}
      <GenericHistorySelector<ProficiencyLevel>
        isOpen={showHistorySelector}
        pastLessons={pastLessonsList}
        onGenerate={handleGenerateLesson}
        onClose={() => setShowHistorySelector(false)}
        customInstructionPlaceholder="e.g., Shopping at a market, Introducing yourself, Talking about family..."
        levelConfig={{
          options: [
            { value: 'beginner', label: 'Beginner (A1-A2)' },
            { value: 'intermediate', label: 'Intermediate (B1-B2)' },
            { value: 'advanced', label: 'Advanced (C1-C2)' }
          ],
          defaultLevel: 'beginner',
          label: 'Select Your Level'
        }}
        extractLessonLevel={extractLevelFromObjective}
        getLevelDisplayName={getLevelDisplayName}
      />

      {/* Word Popup */}
      {selectedWord && (
        <>
          <div className="hindi-popup-overlay" onClick={() => setSelectedWord(null)} />
          <div className="hindi-word-popup">
            <button 
              className="hindi-popup-close" 
              onClick={() => setSelectedWord(null)}
            >
              ×
            </button>
            <div className="hindi-word-character">{selectedWord.character}</div>
            <div className="hindi-word-transliteration">{selectedWord.transliteration}</div>
            <div className="hindi-word-english">{selectedWord.english}</div>
            <div className="hindi-word-characters-section">
              <div className="hindi-word-characters-label">Characters:</div>
              <div className="hindi-word-characters-grid">
                {Array.from(selectedWord.character).map((char, index) => {
                  const transliteration = getCharacterTransliteration(char);
                  return (
                    <div key={index} className="hindi-word-char-item">
                      <span className="hindi-word-char-display">{char}</span>
                      {transliteration && (
                        <span className="hindi-word-char-trans">{transliteration}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
        </>
      )}
    </div>
  );
};

export default HindiReading;

